/**
 * custom.js — Master animation file
 * ──────────────────────────────────────────────────────────────
 * ORGANISATION PATTERN:
 *
 * Every feature is wrapped in an IIFE with a DOM guard at the
 * top that returns early if the required element(s) are absent.
 * This means each block is self-contained and safe to run on
 * ANY page — no errors from missing elements.
 *
 *   ;(function featureName() {
 *     const el = document.querySelector('.required-element');
 *     if (!el) return;           // ← guard: bail if not on this page
 *     // ... feature code ...
 *   })();
 *
 * The semicolons before each IIFE prevent ASI issues when
 * files are concatenated.
 * ──────────────────────────────────────────────────────────────  
 */ 
  
import gsap from '../../js/gsap/gsap.min.js';   
import { ScrollTrigger } from '../../js/gsap/ScrollTrigger.min.js'
window.__ST_custom = ScrollTrigger;  
import { lenis } from '../../js/theme/_lenis.js';   
import "../../js/theme/_swipers.js";
import { MotionPathPlugin } from '../../js/gsap/MotionPathPlugin.min.js';
import { SplitText } from '../../js/gsap/SplitText.min.js';
import { ScrollToPlugin } from '../../js/gsap/ScrollToPlugin.min.js';
import { Flip } from '../../js/gsap/Flip.min.js';
import "../../js/theme/_news-filter.js";
import "../../js/theme/_property-filter.js";
import Splitting from 'splitting';
 
gsap.registerPlugin(MotionPathPlugin, SplitText, ScrollTrigger, ScrollToPlugin, Flip);


ScrollTrigger.config({ ignoreMobileResize: true });

;(function initHeader() {
  const header = document.getElementById("wrapper-navbar");
  if (!header) return;

  header.classList.add("sticky");

  function addClass()    { header.classList.add('scrolling'); }
  function removeClass() { header.classList.remove('scrolling'); }
  ScrollTrigger.addEventListener('scrollStart', addClass);
  ScrollTrigger.addEventListener('scrollEnd', removeClass);

  const showAnim = gsap.from(header, {
    y: -177,
    paused: true,
    duration: 0.8,
    ease: "power2.out"
  }).progress(1);

  let lastState = "";

  ScrollTrigger.create({
    start: '100px',
    end: () => ScrollTrigger.maxScroll(window) + 10,

    onUpdate: (self) => {
      const state =
        self.scroll() < 100 ? "top" :
        self.direction === -1 ? "up" : "down";

      if (state === lastState) return;
      lastState = state;

      if (state === "top") {
        header.classList.remove('up-scroll');
        showAnim.progress(1);
      } else if (state === "up") {
        showAnim.play();
        header.classList.add('up-scroll');
      } else {
        showAnim.reverse();
        header.classList.remove('up-scroll');
      }
    },

    onLeave: () => showAnim.play()
  });
})();
/* ══════════════════════════════════════════════════════════════
   LINE REVEAL
   ══════════════════════════════════════════════════════════════ */
;(function initLineReveal() {
  const lines = document.querySelectorAll('.line-inner');
  if (!lines.length) return;

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        gsap.to(entry.target, {
          width: "52px",
          duration: 1,
          ease: "power2.out"
        });
        obs.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  lines.forEach(line => observer.observe(line));
})();


/* ══════════════════════════════════════════════════════════════
   IMAGE PARALLAX
   ══════════════════════════════════════════════════════════════ */
;(function initImageParallax() {
  const containers = gsap.utils.toArray('.parallax-inner');
  if (!containers.length) return;

  containers.forEach(container => {
    const img = container.querySelector('img');
    if (!img) return;

    gsap.timeline({
      scrollTrigger: {
        trigger: container,
        scrub: true,
        pin: false,
      }
    }).fromTo(img, {
      yPercent: -10,
      ease: 'none'
    }, {
      yPercent: 10,
      ease: 'none'
    });
  });
})();


/* ══════════════════════════════════════════════════════════════
   SECTION FADE-IN OBSERVER
   ══════════════════════════════════════════════════════════════ */
;(function initSectionReveal() {
  const animatedEls = document.querySelectorAll(".animated");
  if (!animatedEls.length) return;

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const delay = entry.target.getAttribute('data-delay');
        if (delay) {
          entry.target.style.animationDelay = delay;
        }
        entry.target.classList.add("fadeInUp");
      }
    });
  }, { root: null });

  animatedEls.forEach((el) => revealObserver.observe(el));
})();


/* ══════════════════════════════════════════════════════════════
   SITE MENU
   ══════════════════════════════════════════════════════════════ */
;(function initSiteMenu() {
  const menu        = document.querySelector('.site-menu');
  const openButton  = document.querySelector('.site-menu-toggle');
  const closeButton = document.querySelector('.site-menu-close');
  if (!menu || !openButton || !closeButton) return;
  gsap.set('.site-menu-close', { opacity: 0, y: -20 });
  gsap.set('.site-menu-nav > ul > li', { opacity: 0, y: 20 });
  gsap.set('.site-menu-social', { opacity: 0, y: 20 }); 
  gsap.set('.site-menu-images-link', { opacity: 0, y: 20 });
  function animateMenuIn() {
    gsap.to('.site-menu-close', { delay: 0.1, duration: 0.6, opacity: 1, y: 0 });
    gsap.to('.site-menu-nav > ul > li', { delay: 0.2, duration: 0.6, opacity: 1, y: 0, stagger: 0.075 });
    gsap.to('.site-menu-social', { delay: 0.8, duration: 0.6, opacity: 1, y: 0 });
    gsap.to('.site-menu-images-link', { delay: 1, duration: 0.6, opacity: 1, y: 0 });
  }

  function animateMenuOut() {
    gsap.to('.site-menu-close', { duration: 0.3, opacity: 0, y: -20 });
    gsap.to('.site-menu-nav > ul > li', { duration: 0.3, opacity: 0, y: -20, stagger: 0.05 });
    gsap.to('.site-menu-social', { duration: 0.3, opacity: 0, y: -20 });
    gsap.to('.site-menu-images-link', { duration: 0.3, opacity: 0, y: -20 });

    setTimeout(() => {
      gsap.set('.site-menu-close', { opacity: 0, y: 20 });
      gsap.set('.site-menu-nav > ul > li', { opacity: 0, y: 20 });
      gsap.set('.site-menu-social', { opacity: 0, y: 20 });
      gsap.set('.site-menu-images-link', { opacity: 0, y: 20 });
    }, 1200);
  }

  openButton.addEventListener('click', () => {
    menu.classList.add('site-menu-is-open');
    document.body.classList.add('no-scroll');
    animateMenuIn();
  });

  closeButton.addEventListener('click', () => {
    menu.classList.remove('site-menu-is-open');
    document.body.classList.remove('no-scroll');
    animateMenuOut();
  });

  document.querySelectorAll('.site-menu-nav-item').forEach(item => {
    item.addEventListener('mouseenter', () => {
      const imageId    = item.dataset.image;
      const activeImg  = document.querySelector('.site-menu-images-item.active');
      const newImg     = document.querySelector('#' + imageId);
      if (activeImg && activeImg !== newImg) { activeImg.classList.remove('active'); }
      if (newImg) { newImg.classList.add('active'); }
    });
  });
})();

/* ══════════════════════════════════════════════════════════════
   SITE MENU — MOBILE SUBMENUS (< 768px) 
   Tapping a nav item that owns a .dropdown-menu slides its
   matching .site-menu-submenu panel over .site-menu-main.
   ══════════════════════════════════════════════════════════════ */
   ;(function initSiteMenuMobileSubmenus() { 
    'use strict';
  
    var MOBILE_QUERY = '(max-width: 767.98px)'; 
    var OPEN_CLASS   = 'is-open';
  
    var menu      = document.querySelector('.site-menu');
    var nav       = menu && menu.querySelector('.site-menu-nav');
    var container = menu && menu.querySelector('.site-menu-images-container');
    if (!menu || !nav || !container) return;
  
    var mediaQuery = window.matchMedia(MOBILE_QUERY);
  
    /**
     * Mirror of WordPress sanitize_title() for the common case.
     * Only used when a nav item is missing its data-image attribute.
     * @param {string} value
     * @returns {string}
     */
    function slugify(value) {
      return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }
  
    /**
     * Does this nav link own a dropdown (i.e. has child menu items)?
     * @param {HTMLElement} link
     * @returns {boolean}
     */
    function hasDropdown(link) {
      var sibling = link.nextElementSibling;
      return Boolean(
        (sibling && sibling.classList.contains('dropdown-menu')) ||
        (link.parentElement && link.parentElement.querySelector(':scope > .dropdown-menu'))
      );
    }
  
    /**
     * Resolve the submenu panel belonging to a nav link.
     * @param {HTMLElement} link
     * @returns {HTMLElement|null}
     */
    function panelFor(link) {
      var id = link.dataset.image || slugify(link.textContent);
      if (!id) return null;
  
      var panel = container.querySelector('[id="' + id.replace(/"/g, '\\"') + '"]');
      return panel && panel.classList.contains('site-menu-submenu') ? panel : null;
    }
  
    /**
     * Close every open panel.
     * @param {HTMLElement} [except] Panel to leave open.
     */
    function closeAll(except) {
      var panels = container.querySelectorAll('.site-menu-submenu.' + OPEN_CLASS);
      Array.prototype.forEach.call(panels, function (panel) {
        if (panel !== except) {
          panel.classList.remove(OPEN_CLASS);
          panel.setAttribute('aria-hidden', 'true');
        }
      });
    }
  
    /**
     * @param {HTMLElement} panel
     */
    function open(panel) {
      closeAll(panel);
      panel.classList.add(OPEN_CLASS);
      panel.setAttribute('aria-hidden', 'false');
  
      var firstLink = panel.querySelector('a[href]');
      if (firstLink) {
        // Defer so the slide-in isn't interrupted by a scroll-into-view jump.
        window.setTimeout(function () {
          try { firstLink.focus({ preventScroll: true }); } catch (error) { firstLink.focus(); }
        }, 500);
      }
    }
  
    /* ── Open: intercept taps on parent nav items ──────────────── */
    nav.addEventListener('click', function (event) {
      if (!mediaQuery.matches) return;
  
      var link = event.target.closest('.site-menu-nav-item');
      if (!link || !nav.contains(link) || !hasDropdown(link)) return;
  
      var panel = panelFor(link);
      if (!panel) return; // No panel: let the link navigate normally.
  
      event.preventDefault();
      event.stopPropagation(); // Keep Bootstrap's dropdown handler out of it.
      open(panel);
    });
  
    /* ── Close: panel back button ──────────────────────────────── */
    container.addEventListener('click', function (event) {
      var button = event.target.closest('.site-menu-submenu-close');
      if (!button || !container.contains(button)) return;
  
      event.preventDefault();
      var panel = button.closest('.site-menu-submenu');
      if (panel) {
        panel.classList.remove(OPEN_CLASS);
        panel.setAttribute('aria-hidden', 'true');
      }
    });
  
    /* ── Close: Escape key ─────────────────────────────────────── */
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAll();
    });
  
    /* ── Close: whenever the main menu closes ──────────────────── */
    var menuCloseButton = document.querySelector('.site-menu-close');
    if (menuCloseButton) menuCloseButton.addEventListener('click', function () { closeAll(); });
  
    /* ── Close: leaving the mobile breakpoint ──────────────────── */
    function handleBreakpointChange(event) {
      if (!event.matches) closeAll();
    }
  
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleBreakpointChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleBreakpointChange); // Safari < 14
    }
  
    /* ── Hover-image behaviour is desktop-only ─────────────────── */
    Array.prototype.forEach.call(nav.querySelectorAll('.site-menu-nav-item'), function (link) {
      var panel = panelFor(link);
      if (panel) panel.setAttribute('aria-hidden', 'true');
    });
  })();


/* ══════════════════════════════════════════════════════════════
   SPLITTING.JS INIT (bundled — no CDN race condition)
   ══════════════════════════════════════════════════════════════ */
   ;(function initSplitting() {
    const targets = document.querySelectorAll('[data-splitting]');
    if (!targets.length) return;
  
    function runSplitting() {
      try {
        targets.forEach(el => {
          if (el.dataset.split) return;          // idempotent — safe if banner split first
          Splitting({ target: el, by: 'chars' });
          el.dataset.split = '1';
        });
        ScrollTrigger.refresh();
      } catch (err) {
        console.error('[initSplitting] Failed:', err);
      }
    }
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runSplitting, { once: true });
    } else {
      runSplitting();
    }
  })();

/* ══════════════════════════════════════════════════════════════
   BANNER REVEAL (plays immediately — no ScrollTrigger)
   ══════════════════════════════════════════════════════════════ */
   ;(function initBannerReveal() {
    const banners = document.querySelectorAll('.banner-reveal');
    if (!banners.length) return;
  
    function runBanner() {
      banners.forEach(banner => {
        if (banner.dataset.revealed) return;
        banner.dataset.revealed = '1';
  
        /* self-split — works regardless of init order */
        if (!banner.dataset.split) {
          Splitting({ target: banner, by: 'chars' });
          banner.dataset.split = '1';
        }
  
        const chars = banner.querySelectorAll('.char');
        if (!chars.length) return;
  
        gsap.fromTo(chars,
          { opacity: 0, y: 10 },
          { duration: 1, stagger: 0.02, opacity: 1, y: 0, delay: 0.1 }
        );
      });
    }
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runBanner, { once: true });
    } else {
      runBanner();
    }
  })();
  

/* ══════════════════════════════════════════════════════════════
   SCROLL REVEAL (fade, text, image wipe) — flattened
   ══════════════════════════════════════════════════════════════ */
   ;(function initScrollReveal() {
    const revealEls = document.querySelectorAll(".reveal-on-scroll");
    if (!revealEls.length) return;
  
    /* ── Text reveal (staggered chars) ── */
    ScrollTrigger.batch(".reveal-on-scroll.reveal-animate-text", {
      start: "top bottom-=100px",
      once: true,
      onEnter: (elements) => {
        elements.forEach((element) => {
          const chars = element.querySelectorAll(".char");
          if (!chars.length) return;
          gsap.to(chars, {
            duration: 1,
            stagger: 0.02,
            opacity: 1,
            y: 0
          });
        });
      }
    });
  
    /* ── Fade in ── */
    ScrollTrigger.batch(".reveal-on-scroll.reveal-fade-in", {
      start: "top bottom-=100px",
      once: true,
      onEnter: (elements) => {
        gsap.to(elements, {
          duration: 0.5,
          stagger: 0.2,          // replaces the old index * 0.2 delay
          opacity: 1,
          y: 0
        });
      }
    });
  
    /* ── Image wipe ── */
    ScrollTrigger.batch(".reveal-on-scroll.reveal-image-wipe", {
      start: "top bottom-=200px",
      once: true,
      onEnter: (elements) => {
        gsap.to(elements, {
          duration: 0.8,
          stagger: 0.2,          // replaces the old index * 0.2 delay
          clipPath: "inset(0% 0% 0% 0%)",
          ease: "power3.inOut"
        });
      }
    });
  })();


/* ══════════════════════════════════════════════════════════════
   PARALLAX WINDOW
   ══════════════════════════════════════════════════════════════ */
   ;(function initParallaxWindow() {
    const windows = document.querySelectorAll(".parallax-window");
    if (!windows.length) return;
  
    windows.forEach(e => {
      const t = e.querySelector(".parallax-image");
      if (!t) return;
      gsap.to(t, {
        scrollTrigger: {
          trigger: e,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
          invalidateOnRefresh: true,   // ← recalc positions on refresh
          //markers: true
        },
        yPercent: 20,
        ease: "none",
        
      });
    });
  })();


/* ══════════════════════════════════════════════════════════════
   IMAGE WIPE INITIAL STATE
   ══════════════════════════════════════════════════════════════ */
;(function initImageWipeState() {
  const wipes = document.querySelectorAll(".reveal-image-wipe");
  if (!wipes.length) return;

  gsap.set(wipes, { clipPath: "inset(0% 0% 100% 0%)" });
})();


/* ══════════════════════════════════════════════════════════════
   PRETITLE CHARACTER ANIMATION
   ══════════════════════════════════════════════════════════════ */
;(function initPretitles() {
  const elements = document.querySelectorAll(".animateText [data-animation='pretitle']");
  if (!elements.length) return;

  function animatePretitles() {
    elements.forEach((element) => {
      element.classList.add("pretitle-animation");

      const split = new SplitText(element, {
        type: "chars, lines",
        charsClass: "char",
      });

      let lastDelay = 0;
      split.chars.forEach((char, index) => {
        char.style.transitionDelay = `${0.015 * index}s`;
        lastDelay = 0.015 * index;
      });

      const cleanupTime = (lastDelay + 2.4) * 1000;

      const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            split.chars.forEach((char) => char.classList.add("active-animation"));
            setTimeout(() => split.revert(), cleanupTime);
            obs.unobserve(entry.target);
          }
        });
      }, { threshold: 0, rootMargin: "0px 0px -8% 0px" });

      observer.observe(element);
    });
  }

  document.addEventListener("DOMContentLoaded", animatePretitles);
})();


;(function initLetterOpacity() {
  const elements = document.querySelectorAll(
    ".letterAnimation [data-animation='letter-opacity']"
  );
  if (!elements.length) return;

  function createAnimation(element) {
    const split = new SplitText(element, {
      type: "chars, words, lines",
      charsClass: "letter-char",
    });

    gsap.set(split.chars, { opacity: 0.2 });

    const isTeamTop = element.closest(".team-top");

    gsap.to(split.chars, {
      opacity: 1,
      stagger: 0.05,
      ease: "none",
      scrollTrigger: {
        trigger: element,
        start: isTeamTop ? "top 30%" : "top 80%",
        end: isTeamTop ? "bottom center" : "top 20%",
        scrub: true,
        invalidateOnRefresh: true,
      },
    });

    return split;
  }

  window.addEventListener("load", () => {
    gsap.matchMedia().add("(min-width: 992px)", () => {
      const splits = [];
      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              obs.unobserve(entry.target);
              splits.push(createAnimation(entry.target));
            }
          });
        },
        { rootMargin: "200px 0px" }
      );

      elements.forEach((el) => observer.observe(el));

      return () => {
        observer.disconnect();
        splits.forEach((s) => s.revert());
      };
    });
  });
})();


/* ══════════════════════════════════════════════════════════════
   ANIMATION CONTAINER LIFT (parallax on reveal)
   ══════════════════════════════════════════════════════════════ */
   ;(function initAnimationContainerLift() {
    const mm = gsap.matchMedia();
  
    mm.add("(min-width: 600px)", () => {
      gsap.utils.toArray(".animation-container").forEach((container) => {
        const hero = container.closest(".collaborations-hero");
        const endEl =
          hero?.nextElementSibling?.classList.contains("home-collections-listings")
            ? hero.nextElementSibling
            : container.parentElement.querySelector(".home-collections-listings");
  
        gsap.fromTo(
          container,
          { y: 50 },
          {
            y: 0,
            ease: "power1.out",
            scrollTrigger: {
              trigger: container,
              start: "top 85%",
              endTrigger: endEl || container,
              end: "top 40%",
              scrub: 0.8,
              invalidateOnRefresh: true,
            },
          }
        );
      });
    });
  })();
/* ══════════════════════════════════════════════════════════════
   FULLSCREEN CAROUSEL
   ══════════════════════════════════════════════════════════════ */
   ;(function initCarousel() {
    if (!document.querySelector('.Caroussel_wrapper')) return;
  
    const SLIDE_DURATION = 500;
    const HOLD_DURATION  = 0.6;
    const VH_PER_SLIDE   = 100;
  
    const wrapper         = document.querySelector('.Caroussel_wrapper');
    const slidesContainer = document.querySelector('.Caroussel_slidesContainer');
    const slides          = gsap.utils.toArray('.Caroussel_slide');
    const images          = gsap.utils.toArray('.Caroussel_slide img');
    const navButtons      = gsap.utils.toArray('.Caroussel_nav button');
    const progressFills   = gsap.utils.toArray('.Caroussel_progressBarFill');
    const textSlides      = gsap.utils.toArray('.TextSlide_textSlide');
    const textContainer   = document.querySelector('.Caroussel_textSlidesContainer');
    const indexCurrent    = document.querySelector('.IndexSlide_indexCurrent');
  
    const totalSlides = slides.length;
    if (!totalSlides) return;
  
    wrapper.classList.add('Caroussel_isSticky');
    wrapper.style.height = (totalSlides + 1) * VH_PER_SLIDE + 'vh';
  
    slides.forEach(function (slide, i) {
      gsap.set(slide, { zIndex: totalSlides - i });
      gsap.set(slide, { clipPath: 'inset(0% 0% 0% 0%)' });
    });
  
    gsap.set(images, { scale: 1.15 });
    gsap.set(textSlides, { autoAlpha: 0, y: 30 });
    gsap.set(textContainer, { autoAlpha: 0 });
    gsap.set(slidesContainer, { scale: 1, clipPath: 'inset(2%)', opacity: 1 });
  
    function setActiveNav(index) {
      navButtons.forEach(function (btn, i) {
        btn.classList.toggle('Caroussel_active', i === index);
      });
      if (indexCurrent) {
        indexCurrent.textContent = String(index + 1).padStart(2, '0');
      }
    }
  
    var tl = gsap.timeline({ paused: true });
    var slideRestTimes = [];
  
    // Intro animation
    tl.to(slidesContainer, { scale: 1, clipPath: 'inset(0%)', opacity: 1, duration: SLIDE_DURATION, ease: 'power2.out' }, 0);
    tl.to(textContainer, { autoAlpha: 1, duration: SLIDE_DURATION * 0.5, ease: 'power1.out' }, SLIDE_DURATION * 0.3);
    tl.to(textSlides[0], { autoAlpha: 1, y: 0, duration: SLIDE_DURATION * 0.6, ease: 'power2.out' }, SLIDE_DURATION * 0.4);
    tl.to(images[0], { scale: 1, duration: SLIDE_DURATION, ease: 'power1.out' }, 0);
  
    // First slide rest point
    var holdStart0 = tl.duration();
    tl.to({}, { duration: HOLD_DURATION });
    slideRestTimes.push(holdStart0 + HOLD_DURATION * 0.5);
  
    for (var i = 0; i < totalSlides - 1; i++) {
      var transitionStart = tl.duration();
  
      tl.to(slides[i], { clipPath: 'inset(0% 0% 100% 0%)', duration: SLIDE_DURATION, ease: 'power3.inOut' }, transitionStart);
      tl.to(images[i], { y: '-15%', duration: SLIDE_DURATION, ease: 'power2.in' }, transitionStart);
      tl.fromTo(images[i + 1], { scale: 1.15, y: '5%' }, { scale: 1, y: '0%', duration: SLIDE_DURATION, ease: 'power2.out' }, transitionStart);
      tl.to(textSlides[i], { autoAlpha: 0, y: -20, duration: SLIDE_DURATION * 0.4, ease: 'power2.in' }, transitionStart);
      tl.fromTo(textSlides[i + 1], { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: SLIDE_DURATION * 0.6, ease: 'power2.out' }, transitionStart + SLIDE_DURATION * 0.4);
  
      if (i < totalSlides - 2) {
        var holdStart = tl.duration();
        tl.to({}, { duration: HOLD_DURATION });
        slideRestTimes.push(holdStart + HOLD_DURATION * 0.5);
      } else {
        slideRestTimes.push(tl.duration());
      }
    }
  
    var totalDuration = tl.duration();
  
    var mainST = ScrollTrigger.create({
      trigger: wrapper,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 1,
      onUpdate: function (self) {
        tl.progress(self.progress);
  
        var currentTime  = self.progress * totalDuration;
        var activeIndex  = 0;
  
        for (var k = slideRestTimes.length - 1; k >= 0; k--) {
          var threshold = k === 0 ? 0 : (slideRestTimes[k - 1] + slideRestTimes[k]) * 0.5;
          if (currentTime >= threshold) {
            activeIndex = k;
            break;
          }
        }
  
        setActiveNav(activeIndex);
  
        progressFills.forEach(function (fill, idx) {
          if (idx < activeIndex) { gsap.set(fill, { scaleX: 1 }); }
          else if (idx === activeIndex) {
            var segStart = idx === 0 ? 0 : (slideRestTimes[idx - 1] + slideRestTimes[idx]) * 0.5;
            var segEnd   = idx < slideRestTimes.length - 1 ? (slideRestTimes[idx] + slideRestTimes[idx + 1]) * 0.5 : totalDuration;
            var segProgress = gsap.utils.clamp(0, 1, (currentTime - segStart) / (segEnd - segStart));
            gsap.set(fill, { scaleX: segProgress });
          }
          else { gsap.set(fill, { scaleX: 0 }); }
        });
      },
    });
  
    navButtons.forEach(function (btn, i) {
      btn.addEventListener('click', function () {
        var targetProgress = slideRestTimes[i] / totalDuration;
        var scrollTarget   = mainST.start + (mainST.end - mainST.start) * targetProgress;
        var maxScroll      = document.documentElement.scrollHeight - window.innerHeight;
        scrollTarget       = Math.max(0, Math.min(scrollTarget, maxScroll));
        window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
      });
    });
  
    if (typeof window.__carouselCleanup === 'function') { window.__carouselCleanup(); }
    window.__carouselCleanup = function () {
      tl.kill();
      ScrollTrigger.getAll().forEach(function (st) { st.kill(); });
    };
  })();


/* ══════════════════════════════════════════════════════════════
   ACCORDION CAROUSEL
   ══════════════════════════════════════════════════════════════ */
;(function initAccordionCarousel() {
  if (!document.querySelector('.AccordionCarousel_wrapper')) return;

  const TRANSITION_DURATION = 500;
  const HOLD_DURATION       = 0.6;
  const VH_PER_SLIDE        = 100;

  const wrapper    = document.querySelector('.AccordionCarousel_wrapper');
  const items      = gsap.utils.toArray('.AccordionCarousel_item[data-accordion-index]');
  const headers    = gsap.utils.toArray('.AccordionCarousel_header');
  const bodies     = gsap.utils.toArray('.AccordionCarousel_body');
  const bodyInners = gsap.utils.toArray('.AccordionCarousel_bodyInner');
  const imgSlides  = gsap.utils.toArray('.AccordionCarousel_imageSlide[data-slide-index]');

  const totalSlides = items.length;
  if (!totalSlides) return;

  wrapper.style.height = (totalSlides + 1) * VH_PER_SLIDE + 'vh';

  const bodyHeights = bodies.map(function (body) {
    body.style.height = 'auto';
    var h = body.scrollHeight;
    body.style.height = '0px';
    return h;
  });

  gsap.set(bodyInners, { opacity: 0, y: 12 });
  gsap.set(imgSlides, { clipPath: 'inset(0% 0% 100% 0%)' });
  gsap.set(imgSlides[0], { clipPath: 'inset(0% 0% 0% 0%)' });

  function setActiveHeader(index) {
    headers.forEach(function (btn, i) {
      btn.classList.toggle('AccordionCarousel_active', i === index);
    });
  }

  var tl = gsap.timeline({ paused: true });
  var slideRestTimes = [];

  tl.to(bodies[0], { height: bodyHeights[0], duration: TRANSITION_DURATION * 0.6, ease: 'power2.out' }, 0);
  tl.to(bodyInners[0], { opacity: 1, y: 0, duration: TRANSITION_DURATION * 0.5, ease: 'power2.out' }, TRANSITION_DURATION * 0.15);
  tl.to(imgSlides[0], { clipPath: 'inset(0% 0% 0% 0%)', duration: TRANSITION_DURATION, ease: 'power3.inOut' }, 0);

  var holdStart0 = tl.duration();
  tl.to({}, { duration: HOLD_DURATION });
  slideRestTimes.push(holdStart0 + HOLD_DURATION * 0.5);

  for (var i = 0; i < totalSlides - 1; i++) {
    var transitionStart = tl.duration();

    tl.to(bodyInners[i], { opacity: 0, y: -8, duration: TRANSITION_DURATION * 0.3, ease: 'power2.in' }, transitionStart);
    tl.to(bodies[i], { height: 0, duration: TRANSITION_DURATION * 0.5, ease: 'power2.inOut' }, transitionStart + TRANSITION_DURATION * 0.15);
    tl.to(bodies[i + 1], { height: bodyHeights[i + 1], duration: TRANSITION_DURATION * 0.5, ease: 'power2.out' }, transitionStart + TRANSITION_DURATION * 0.4);
    tl.to(bodyInners[i + 1], { opacity: 1, y: 0, duration: TRANSITION_DURATION * 0.5, ease: 'power2.out' }, transitionStart + TRANSITION_DURATION * 0.55);
    tl.to(imgSlides[i], { clipPath: 'inset(0% 0% 100% 0%)', duration: TRANSITION_DURATION, ease: 'power3.inOut' }, transitionStart);
    tl.fromTo(imgSlides[i + 1], { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: TRANSITION_DURATION, ease: 'power3.inOut' }, transitionStart);

    if (i < totalSlides - 2) {
      var holdStart = tl.duration();
      tl.to({}, { duration: HOLD_DURATION });
      slideRestTimes.push(holdStart + HOLD_DURATION * 0.5);
    } else {
      slideRestTimes.push(tl.duration());
    }
  }

  var totalDuration = tl.duration();

  var mainST = ScrollTrigger.create({
    trigger: wrapper,
    start: 'top top',
    end: 'bottom bottom',
    scrub: 1,
    onUpdate: function (self) {
      tl.progress(self.progress);

      var currentTime  = self.progress * totalDuration;
      var currentIndex = 0;

      for (var k = slideRestTimes.length - 1; k >= 0; k--) {
        var threshold = k === 0 ? 0 : (slideRestTimes[k - 1] + slideRestTimes[k]) * 0.5;
        if (currentTime >= threshold) {
          currentIndex = k;
          break;
        }
      }
      setActiveHeader(currentIndex);
    },
  });

  headers.forEach(function (btn, i) {
    btn.style.cursor = 'pointer';
    btn.addEventListener('click', function () {
      var targetProgress = slideRestTimes[i] / totalDuration;
      var scrollTarget   = mainST.start + (mainST.end - mainST.start) * targetProgress;
      var maxScroll      = document.documentElement.scrollHeight - window.innerHeight;
      scrollTarget       = Math.max(0, Math.min(scrollTarget, maxScroll));
      window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
    });
  });

  var resizeTimer;
  window.addEventListener('resize', function () { 
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      bodies.forEach(function (body, idx) {
        var wasOpen = parseFloat(body.style.height) > 0;
        body.style.height = 'auto';
        bodyHeights[idx] = body.scrollHeight;
        body.style.height = wasOpen ? bodyHeights[idx] + 'px' : '0px';
      }); 
      ScrollTrigger.refresh();
    }, 250);
  });

  if (typeof window.__accordionCarouselCleanup === 'function') { window.__accordionCarouselCleanup(); }
  window.__accordionCarouselCleanup = function () {
    tl.kill();
    ScrollTrigger.getAll().forEach(function (st) { st.kill(); });
  };
})();


/* ══════════════════════════════════════════════════════════════
   TEAM MEMBER POPUP
   ══════════════════════════════════════════════════════════════ */
;(function initTeamPopupBoot() {
  document.addEventListener('DOMContentLoaded', function () {
    var teamContainers = document.querySelectorAll('.team__content');
    if (!teamContainers.length) return;

    var popup = document.querySelector('.team-member-popup');
    if (!popup) return;

    window.__teamPopupCleanup = initTeamPopup(document);
  });

  // ── The full initTeamPopup function ──
  // (Keeping the original implementation intact — it already
  //  has internal null checks on popup elements.)

  function initTeamPopup(container) {
    var popup = document.querySelector('.team-member-popup');
    if (!popup || !container) return function () {};

    var popupInner         = popup.querySelector('.team-member');
    var popupDataContainer = popupInner ? popupInner.querySelector('.team-member__data') : null;
    var closeTeamButton    = popup.querySelector('.team-member-popup__close');
    var teamListItems      = container.querySelectorAll('.team__entry');

    if (!popupInner || !popupDataContainer || !closeTeamButton || !teamListItems.length) {
      return function () {};
    }

    var currentIndex      = null;
    var urlsByIndex        = [];
    var cachedPages        = {};
    var navigationButtons  = {
      prev: popup.querySelector('.team-member__button--prev'),
      next: popup.querySelector('.team-member__button--next'),
    };
    var popupElements = getPopupElements(popup);

    popupInner.style.setProperty('--total', teamListItems.length);

    if (navigationButtons.prev) navigationButtons.prev.addEventListener('click', goPrevious);
    if (navigationButtons.next) navigationButtons.next.addEventListener('click', goNext);

    var cleanupClickHandlers = Array.from(teamListItems).map(function (entry, index) {
      var link = entry.querySelector('a');
      if (!link) return function () {};

      var href = link.getAttribute('href');
      urlsByIndex[index] = href;

      var imageContainer = entry.querySelector('.team__entry__images');

      function handleClick(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (!href || currentIndex === index) return;
        openTeamMember(href, index, imageContainer);
      }

      link.addEventListener('click', handleClick, { capture: true });
      return function () { link.removeEventListener('click', handleClick, { capture: true }); };
    });

    closeTeamButton.addEventListener('click', closePopup);

    function handleKeydown(event) {
      if (event.key === 'Escape' && currentIndex !== null) closePopup();
    }
    document.addEventListener('keydown', handleKeydown);

    return function () {
      cleanupClickHandlers.forEach(function (fn) { fn(); });
      closeTeamButton.removeEventListener('click', closePopup);
      document.removeEventListener('keydown', handleKeydown);
      if (navigationButtons.prev) navigationButtons.prev.removeEventListener('click', goPrevious);
      if (navigationButtons.next) navigationButtons.next.removeEventListener('click', goNext);
    };

    /* ── Helpers (hoisted) ── */

    function disableScroll() {
      var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      document.body.style.paddingRight = scrollbarWidth + 'px';
      var header = document.querySelector('.site-header');
      if (header) header.style.paddingRight = scrollbarWidth + 'px';
    }

    function enableScroll() {
      document.documentElement.style.overflow = '';
      document.body.style.touchAction = '';
      document.body.style.paddingRight = '';
      var header = document.querySelector('.site-header');
      if (header) header.style.paddingRight = '';
    }

    function detectDesktop() { return window.innerWidth > 991; }

    function openTeamMember(url, index, clickedImage) {
      currentIndex = index;
      popupInner.style.setProperty('--current', currentIndex + 1);
      gsap.set(popupElements.card, { opacity: 0 });
      gsap.set(popupElements.content, { opacity: 0 });

      Promise.all([fetchTeamMemberPage(url), animateClickedImage(clickedImage)])
        .then(function (results) {
          var pageElement = results[0];
          var movingImage = results[1];
          if (!pageElement) { closePopup(); return; }
          renderTeamMember(pageElement, { movingImage: movingImage, isDesktop: detectDesktop() });
        })
        .catch(function () { closePopup(); });
    }

    function renderTeamMember(pageElement, options) {
      var isForwards  = options.isForwards !== undefined ? options.isForwards : true;
      var movingImage = options.movingImage || null;
      var isDesktop   = options.isDesktop !== undefined ? options.isDesktop : true;

      popupInner.scrollTo(0, 0);
      popupDataContainer.scrollTo(0, 0);

      var newCard    = pageElement.querySelector('.team-member__card');
      var newContent = pageElement.querySelector('.team-member__content');

      if (newCard) animateCardSwap(newCard, { isStatic: movingImage !== null, isForwards: isForwards, isDesktop: isDesktop });
      if (newContent) popupElements.content.innerHTML = newContent.innerHTML;

      gsap.to([popupElements.card, popupElements.content], { opacity: 1, duration: 0.4 });

      if (movingImage) {
        gsap.timeline()
          .to(movingImage, { opacity: 0, duration: 0.4, delay: 0.4 })
          .add(function () { if (movingImage.parentNode) movingImage.remove(); });
      }
    }

    function animateCardSwap(cardElement, options) {
      var isStatic   = options.isStatic || false;
      var isForwards = options.isForwards !== undefined ? options.isForwards : true;
      var isDesktop  = options.isDesktop !== undefined ? options.isDesktop : true;

      if (isStatic || !isDesktop) {
        if (!isDesktop) gsap.set(cardElement, { opacity: 0 });
        popupElements.card.remove();
        popupElements.cardWrapper.appendChild(cardElement);
        popupElements.card = cardElement;
        return;
      }

      if (isForwards) {
        popupElements.cardWrapper.appendChild(cardElement);
      } else {
        popupElements.cardWrapper.insertBefore(cardElement, popupElements.card);
        gsap.set([popupElements.card, cardElement], { transform: 'translateX(-100%)' });
      }

      var targetX = isForwards ? '-100%' : '0%';

      return gsap.timeline()
        .to([popupElements.card, cardElement], { transform: 'translateX(' + targetX + ')', duration: 0.8 })
        .add(function () {
          popupElements.card.remove();
          gsap.set(cardElement, { transform: 'translateX(0)' });
          popupElements.card = cardElement;
        });
    }

    function goNext(event) {
      event.preventDefault();
      navigateTo((currentIndex + 1) % urlsByIndex.length, true);
    }

    function goPrevious(event) {
      event.preventDefault();
      navigateTo((currentIndex - 1 + urlsByIndex.length) % urlsByIndex.length, false);
    }

    function navigateTo(index, isForwards) {
      var isDesktop = detectDesktop();

      Promise.all([fetchTeamMemberPage(urlsByIndex[index]), fadeOutPopupContent(isDesktop)])
        .then(function (results) {
          var pageElement = results[0];
          if (!pageElement) return;
          renderTeamMember(pageElement, { isForwards: isForwards, isDesktop: isDesktop });
          currentIndex = index;
          popupInner.style.setProperty('--current', currentIndex + 1);
        })
        .catch(function (err) { console.error('[TeamPopup] Navigation error:', err); });

      if (teamListItems[index]) {
        teamListItems[index].scrollIntoView({ block: 'center', inline: 'nearest' });
      }
    }

    function fadeOutPopupContent(isDesktop) {
      return gsap.to([popupElements.content], { opacity: 0, duration: 0.4 });
    }

    function animateClickedImage(imageElement) {
      if (!imageElement) {
        disableScroll();
        popup.classList.add('is-open');
        gsap.set(popup, { opacity: 0 });
        return gsap.to(popup, { opacity: 1, duration: 0.6 }).then(function () { return null; });
      }

      var clone = imageElement.cloneNode(true);
      var rect  = imageElement.getBoundingClientRect();

      gsap.set(clone, {
        position: 'fixed', zIndex: 9999, top: 0, left: 0,
        x: rect.x, y: rect.y, width: rect.width, height: rect.height,
        pointerEvents: 'none',
      });

      document.body.appendChild(clone);
      disableScroll();
      popup.classList.add('is-open');
      gsap.set(popup, { opacity: 0 });
      popup.offsetHeight; // force reflow

      var placeholder = popup.querySelector('.team-member__person__placeholder');
      if (!placeholder) {
        return gsap.to(popup, { opacity: 1, duration: 0.6 }).then(function () { return clone; });
      }

      var targetRect = placeholder.getBoundingClientRect();
      var animTl = gsap.timeline();
      animTl.to(clone, { x: targetRect.x, y: targetRect.y, width: targetRect.width, height: targetRect.height, duration: 0.9, ease: 'power3.inOut' }, 0);
      animTl.to(popup, { opacity: 1, duration: 0.6, ease: 'power1.in' }, 0.35);
      return animTl.then(function () { return clone; });
    }

    function closePopup() {
      return gsap.timeline()
        .to(popup, { opacity: 0, duration: 0.8 })
        .add(function () {
          popup.classList.remove('is-open');
          currentIndex = null;
          enableScroll();
          popupInner.scrollTo(0, 0);
          popupDataContainer.scrollTo(0, 0);
          popupElements.content.innerHTML = '';
          gsap.set(popup, { clearProps: 'opacity' });
        });
    }

    function fetchTeamMemberPage(url) {
      if (!url) return Promise.resolve(null);
      if (cachedPages[url]) return Promise.resolve(cachedPages[url].cloneNode(true));

      return fetch(url)
        .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
        .then(function (html) {
          var doc  = new DOMParser().parseFromString(html, 'text/html');
          var page = doc.querySelector('.team-member');
          if (!page) return null;
          cachedPages[url] = page;
          return page.cloneNode(true);
        })
        .catch(function (err) { console.error('[TeamPopup] Fetch error:', err); return null; });
    }

    function getPopupElements(popupRoot) {
      var emailLink    = popupRoot.querySelector('#team-member-email');
      var phoneLink    = popupRoot.querySelector('#team-member-phone');
      var linkedinLink = popupRoot.querySelector('#team-member-linkedin');
      return {
        cardWrapper:   popupRoot.querySelector('.team-member__card-wrapper'),
        card:          popupRoot.querySelector('.team-member__card'),
        person:        popupRoot.querySelector('.team-member__person'),
        emailLink:     emailLink,
        emailLabel:    emailLink ? emailLink.querySelector('p') : null,
        phoneLink:     phoneLink,
        phoneLabel:    phoneLink ? phoneLink.querySelector('p') : null,
        linkedinLink:  linkedinLink,
        linkedinLabel: linkedinLink ? linkedinLink.querySelector('p') : null,
        content:       popupRoot.querySelector('.team-member__content'),
      };
    }
  }
})();


/* ══════════════════════════════════════════════════════════════
   GRADIENT CANVAS
   ══════════════════════════════════════════════════════════════ */
/* eslint-disable-next-line no-void */
void function initGradientCanvas() {
  var canvas     = document.getElementById("glowCanvas");
  var wrapper    = document.querySelector(".gradient-wrapper");
  var headingBox = document.getElementById("headingContainer");
  if (!canvas || !wrapper || !headingBox) return;

  var ctx          = canvas.getContext("2d");
  var headingWhite = document.getElementById("headingWhite");
  var headingBlack = document.getElementById("headingBlack");

  var dpr = Math.min(window.devicePixelRatio || 1, 2);

  function sizeCanvas() {
    var rect = wrapper.getBoundingClientRect();
    canvas.width  = rect.width  * dpr;
    canvas.height = rect.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  sizeCanvas();
  window.addEventListener("resize", function () {
    sizeCanvas();
    drawGradient(lastProgress);
  });

  var lastProgress = 0;

  function drawGradient(progress) {
    lastProgress = progress;
    var w = canvas.width / dpr;
    var h = canvas.height / dpr;

    ctx.fillStyle = "#F4F1EB";
    ctx.fillRect(0, 0, w, h);
    if (progress <= 0) return;

    var cx = w / 2;
    var cy = h;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(1, 0.6);

    var maxDist     = Math.sqrt(cx * cx + h * h);
    var whiteRadius = maxDist * 1.6 * progress;
    var glowRadius  = maxDist * 2.0 * progress;
    var glowOpacity = Math.max(1 - progress * 1.2, 0);

    if (glowRadius > 1 && glowOpacity > 0.01) {
      var gg = ctx.createRadialGradient(0, 0, 0, 0, 0, glowRadius);
      gg.addColorStop(0, "rgba(160,210,255," + (0.9 * glowOpacity) + ")");
      gg.addColorStop(1, "rgba(237, 156, 159, 0)");
      ctx.fillStyle = gg;
      ctx.fillRect(-w, -h * 2, w * 2, h * 3);
    }

    if (whiteRadius > 1) {
      var wg = ctx.createRadialGradient(0, 0, 0, 0, 0, whiteRadius);
      var solidEdge = 0.25 + progress * 0.6;
      var feather   = Math.min(solidEdge + 0.5, 1);
      wg.addColorStop(0, "#fff");
      wg.addColorStop(solidEdge, "#fff");
      wg.addColorStop(feather, "rgba(237, 156, 159,0)");
      ctx.fillStyle = wg;
      ctx.fillRect(-w, -h * 2, w * 2, h * 3);
    }

    ctx.restore();

    if (progress >= 0.98) {
      var finalAlpha = (progress - 0.98) / 0.02;
      ctx.fillStyle = "rgba(255,255,255," + finalAlpha + ")";
      ctx.fillRect(0, 0, w, h);
    }
  }

  drawGradient(0);

  ScrollTrigger.create({
    trigger: wrapper,
    start: "top center",
    endTrigger: headingBox,
    end: "top 20%",
    scrub: 1,
    onUpdate: function (self) {
      drawGradient(self.progress);
      if (headingWhite) headingWhite.style.opacity = String(1 - self.progress);
      if (headingBlack) headingBlack.style.opacity = String(self.progress);
    },
  });
}();


/* ══════════════════════════════════════════════════════════════
   PROJECT STATUS TIMELINE
   ══════════════════════════════════════════════════════════════ */
   ;(function initProjectTimelines() {
    const timelines = document.querySelectorAll(".timeline[data-current-status]");
    if (!timelines.length) return;
   
    /**
     * Measure the pixel distance between the vertical centres
     * of two timeline circles.
     *
     * @param {HTMLElement} circleA - Starting circle element
     * @param {HTMLElement} circleB - Target circle element
     * @returns {number} Pixel distance between centres
     */
    function distanceBetween(circleA, circleB) {
      const rectA = circleA.getBoundingClientRect();
      const rectB = circleB.getBoundingClientRect();
      return (rectB.top + rectB.height / 2) - (rectA.top + rectA.height / 2);
    }
   
    /**
     * Build and play the sequential GSAP timeline animation.
     * Sequence: circle fill + tick → line → next circle fill + tick → line
     * After completion, adds .checkbox-glow to the current-status circle.
     *
     * @param {HTMLElement} timeline - The .timeline container
     */
    function animateTimeline(timeline) {
      const currentStatus = parseInt(timeline.dataset.currentStatus, 10);
      if (isNaN(currentStatus) || currentStatus < 0) return;
   
      const items    = Array.from(timeline.querySelectorAll(".timeline-item"));
      const fillEl   = timeline.querySelector(".timeline-track__fill");
      const maxIndex = Math.min(currentStatus, items.length - 1);
   
      const tl = gsap.timeline({
        defaults: { ease: "power2.out" },
        onComplete: function () {
          // Add glow to the current-status circle once the full sequence finishes
          const currentCircle = items[maxIndex].querySelector(".timeline-circle");
          if (currentCircle) {
            currentCircle.classList.add("checkbox-glow");
          }
        }
      });
   
      let cumulativeHeight = 0;
   
      for (let i = 0; i <= maxIndex; i++) {
        const circle = items[i].querySelector(".timeline-circle");
        const check  = items[i].querySelector(".timeline-circle__check");
        const path   = check ? check.querySelector("path") : null;
   
        // Step A: Fill the circle
        tl.to(circle, {
          backgroundColor: "var(--color-fill)",
          borderColor:     "var(--color-fill)",
          duration:        0.2,
        });
   
        // Step B: Draw the checkmark
        if (check && path) {
          tl.to(check, { opacity: 1, duration: 0.15 }, "-=0.1");
          tl.to(path, { strokeDashoffset: 0, duration: 0.35 }, "-=0.05");
        }
   
        // Step C: Grow line to next circle
        if (i < maxIndex) {
          const nextCircle    = items[i + 1].querySelector(".timeline-circle");
          const segmentHeight = distanceBetween(circle, nextCircle);
          cumulativeHeight += segmentHeight;
   
          tl.to(fillEl, {
            height:   cumulativeHeight + "px",
            duration: 0.2,
            ease:     "power1.inOut",
          });
        }
      }
    }
   
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          try { animateTimeline(entry.target); }
          catch (err) { console.error("Timeline animation error:", err); }
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.15 }
    );
   
    timelines.forEach((el) => observer.observe(el));
  })();

/* ══════════════════════════════════════════════════════════════
   VIDEO BLOCK (pinned reveal — responsive, multi-instance)
   ══════════════════════════════════════════════════════════════ */
   ;(function initVideoBlocks() {
    'use strict';
  
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.warn('[video_block] GSAP / ScrollTrigger not found — aborting init.');
      return;
    }
  
    /**
     * Responsive configuration.
     * Values interpolate linearly between minWidth and maxWidth,
     * clamped at both ends.
     */
    const CONFIG = {
      minWidth: 600,    // smallest supported viewport (px)
      maxWidth: 1440,   // desktop reference viewport (px)
      clipMin: 12,      // clip-path inset (%) at minWidth
      clipMax: 20,      // clip-path inset (%) at maxWidth
      heightMin: 90,    // container height (vh) at minWidth
      heightMax: 100,   // container height (vh) at maxWidth
      wrapperMultiplier: 2, // wrapper height = container height × this (scrub distance)
    };
  
    /**
     * Linearly interpolate a value based on current viewport width.
     * @param {number} min - Value at CONFIG.minWidth.
     * @param {number} max - Value at CONFIG.maxWidth.
     * @returns {number} Interpolated value, clamped to [min, max].
     */
    function fluidValue(min, max) {
      const vw = window.innerWidth;
      const t = (vw - CONFIG.minWidth) / (CONFIG.maxWidth - CONFIG.minWidth);
      return min + (max - min) * Math.min(1, Math.max(0, t));
    }
  
    /** @returns {number} Current clip inset percentage for this viewport. */
    function getClipInset() {
      return fluidValue(CONFIG.clipMin, CONFIG.clipMax);
    }
  
    /** @returns {number} Current container height in vh for this viewport. */
    function getContainerVh() {
      return fluidValue(CONFIG.heightMin, CONFIG.heightMax);
    }
  
    const wrappers = gsap.utils.toArray('.video_block_wrapper');
    if (!wrappers.length) return;
  
    /**
     * Write responsive CSS custom properties onto each wrapper.
     * Runs before every ScrollTrigger measurement pass (refreshInit),
     * so pin/scrub distances are always based on fresh dimensions.
     */
    function applyResponsiveSizing() {
      const vh = getContainerVh();
      wrappers.forEach((wrapper) => {
        wrapper.style.setProperty('--vb-height', vh + 'vh');
        wrapper.style.setProperty(
          '--vb-wrapper-height',
          vh * CONFIG.wrapperMultiplier + 'vh'
        );
      });
    }
  
    // Ensure sizing is correct BEFORE ScrollTrigger measures (initial + every refresh).
    ScrollTrigger.addEventListener('refreshInit', applyResponsiveSizing);
    applyResponsiveSizing();
  
    wrappers.forEach((wrapper) => {
      try {
        const container       = wrapper.querySelector('.video_block_container');
        const slidesContainer = wrapper.querySelector('.video_block_slidesContainer');
        const image           = wrapper.querySelector('.styles_image');
        const textContainer   = wrapper.querySelector('.video_block_textContainer');
  
        if (!container || !slidesContainer || !image) return; // skip incomplete instances
  
        gsap.set(image, { scale: 1.15 });
        gsap.set(slidesContainer, {
          clipPath: () => 'inset(' + getClipInset() + '%)',
        });
  
        if (textContainer) {
          gsap.set(textContainer, { autoAlpha: 0, y: 20 });
        }
  
        const tl = gsap.timeline();
  
        tl.fromTo(
          slidesContainer,
          // Function-based start value — re-evaluated on every refresh
          // thanks to invalidateOnRefresh, so resizing mid-page recalculates
          // the correct inset for the new viewport width.
          { clipPath: () => 'inset(' + getClipInset() + '%)' },
          { clipPath: 'inset(0%)', ease: 'none', duration: 1 },
          0
        );
  
        tl.to(image, { scale: 1, ease: 'none', duration: 1 }, 0);
  
        if (textContainer) {
          tl.to(
            textContainer,
            { autoAlpha: 1, y: 0, ease: 'power2.out', duration: 0.3 },
            0.35
          );
        }
  
        ScrollTrigger.create({
          trigger: wrapper,
          start: 'top top',
          end: 'bottom bottom',
          pin: container,
          scrub: true,
          animation: tl,
          invalidateOnRefresh: true, // re-reads function-based values on resize
        });
      } catch (err) {
        console.error('[video_block] Failed to initialise instance:', err, wrapper);
      }
    });
  })();

/* ══════════════════════════════════════════════════════════════
   SCROLLTRIGGER REFRESH (after layout settles)
   ══════════════════════════════════════════════════════════════ */
   ;(function refreshScrollTriggers() {
    // baseline refresh once the load event fires
    window.addEventListener("load", () => ScrollTrigger.refresh());
  
    // re-refresh as lazy/late images come in (debounced)
    let timer;
    const queueRefresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => ScrollTrigger.refresh(), 200);
    };
  
    document.querySelectorAll("img").forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", queueRefresh, { once: true });
        img.addEventListener("error", queueRefresh, { once: true });
      }
    });
  })();
